import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Pill from '../common/display/Pill';
import { Button } from '../common/ui/Button';
import Avatar from '../common/display/Avatar';
import { supabase } from '../../lib/supabase';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../utils/posthogUtils';

interface Member {
  user_id: string;
  first_name: string;
  last_name: string;
  profile_image_url: string | null;
}

interface CommunityCardProps {
  id: string;
  title: string;
  variant: 'entertainment' | 'enterprise' | 'retail';
  className?: string;
  showShape?: boolean;
  members: Member[];
  totalMembers: number;
  popularText?: string;
}

const CommunityCard: React.FC<CommunityCardProps> = ({
  id,
  title,
  variant,
  className = '',
  showShape = false,
  members,
  totalMembers,
  popularText
}) => {
  const bgColors = {
    entertainment: 'bg-secondary-50',
    enterprise: 'bg-primary-50',
    retail: 'bg-greyscale-50' // Using greyscale-50 from tailwind config
  };

  const pillColors = {
    entertainment: 'bg-secondary-100 text-secondary-base',
    enterprise: 'bg-primary-100 text-primary-base',
    retail: 'bg-[#D1F3EC] text-[#0BAD86]' // Custom color for retail pill
  };

  const buttonVariants = {
    entertainment: { color: 'secondary' as const, variant: 'filled' as const },
    enterprise: { color: 'primary' as const, variant: 'filled' as const },
    retail: { color: 'success' as const, variant: 'filled' as const }
  };

  const maxVisibleAvatars = 5;
  const maxVisibleAvatarsMobile = 2;
  const displayedMembers = members.slice(0, maxVisibleAvatars);
  const displayedMembersMobile = members.slice(0, maxVisibleAvatarsMobile);
  const remainingMembers = totalMembers - maxVisibleAvatars;
  const remainingMembersMobile = totalMembers - maxVisibleAvatarsMobile;

  return (
    <Link
      to={`/communities/${id}`}
      onClick={() => {
        captureCustomEvent('community_discovery', {
          eventProperties: {
            community_id: id,
            community_name: title,
            source: 'dashboard_recommendation',
            variant: variant,
            position: showShape ? 'featured' : 'regular'
          }
        });
      }}
      className={`relative overflow-hidden rounded-2xl ${bgColors[variant]} ${className} block h-full`}
    >
      <div className="flex flex-col h-full p-6">
        <div className="flex-none mb-6">
          <Pill
            text="Featured"
            className={`${pillColors[variant]} text-sm`}
          />
        </div>

        <h3 className="flex-none text-h8 font-bold text-greyscale-900 mb-2 hyphens-auto break-words overflow-hidden">{title}</h3>

        {popularText && (
          <div className={`mb-4 text-greyscale-600 text-sm ${variant === 'retail' ? 'max-w-[50%]' : ''}`}>
            <span>{popularText}</span>
          </div>
        )}

        {/* Members directly below the text */}
        <div className="mb-8 relative z-20">
          {/* Desktop view - show up to 5 avatars */}
          <div className="hidden sm:flex -space-x-2">
            {displayedMembers.map((member) => (
              <Avatar
                key={member.user_id}
                firstName={member.first_name}
                lastName={member.last_name}
                profileImageUrl={member.profile_image_url || undefined}
                size="small"
                className="border-2 border-white flex-shrink-0"
              />
            ))}
            {remainingMembers > 0 && (
              <div className="relative w-10 h-10 min-w-10 flex-shrink-0 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full border-2 border-white">
                +{remainingMembers}
              </div>
            )}
          </div>
          
          {/* Mobile view - show maximum 2 avatars */}
          <div className="flex sm:hidden -space-x-2">
            {displayedMembersMobile.map((member) => (
              <Avatar
                key={member.user_id}
                firstName={member.first_name}
                lastName={member.last_name}
                profileImageUrl={member.profile_image_url || undefined}
                size="small"
                className="border-2 border-white flex-shrink-0"
              />
            ))}
            {remainingMembersMobile > 0 && (
              <div className="relative w-10 h-10 min-w-10 flex-shrink-0 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full border-2 border-white">
                +{remainingMembersMobile}
              </div>
            )}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-grow"></div>

        {/* Button at the bottom */}
        <div className="flex-none relative z-15">
          <span className="inline-block">
            <Button
              size="small"
              color={buttonVariants[variant].color}
              variant={buttonVariants[variant].variant}
            >
              Explore
            </Button>
          </span>
        </div>
      </div>

      {showShape && variant === 'entertainment' && (
        <svg
          width="120"
          height="160"
          viewBox="0 0 76 110"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute bottom-0 right-0 transform translate-x-6 translate-y-12 z-10"
        >
          <g clipPath="url(#clip0_entertainment)">
            <path
              d="M71.0429 0.277344C31.8028 0.277344 0.000725172 32.0794 0.00072564 71.3195C0.000726108 110.56 31.8028 142.362 71.0429 142.362C72.9246 142.362 74.7866 142.263 76.6387 142.124L76.6387 103.815C74.8163 104.122 72.9543 104.3 71.0429 104.3C52.8292 104.3 38.0721 89.5331 38.0721 71.3294C38.0721 53.1256 52.8292 38.3388 71.0429 38.3388C72.9543 38.3388 74.8163 38.5072 76.6387 38.8241L76.6387 0.515042C74.7866 0.366481 72.9246 0.277344 71.0429 0.277344Z"
              fill="#B77EFF"
            />
            <path
              d="M38.0721 71.3166C38.0721 89.5302 52.8391 104.287 71.0429 104.287C72.9543 104.287 74.8163 104.119 76.6387 103.802L76.6387 38.8212C74.8163 38.5142 72.9543 38.3359 71.0429 38.3359C52.8292 38.3359 38.0721 53.103 38.0721 71.3067L38.0721 71.3166Z"
              fill="#D6B4FF"
            />
          </g>
          <defs>
            <clipPath id="clip0_entertainment">
              <rect width="110" height="76" fill="white" transform="translate(76) rotate(90)" />
            </clipPath>
          </defs>
        </svg>
      )}

      {showShape && variant === 'retail' && (
        <img
          src="https://azzcpbslaipddvocsrcx.supabase.co/storage/v1/object/public/assets//aixr-dots.svg"
          alt="Dots pattern"
          className="absolute bottom-0 right-0 h-[60%] w-auto object-contain transform rotate-90 md:top-0 md:bottom-auto md:rotate-0 md:h-full z-10"
        />
      )}
    </Link>
  );
};

interface RecommendedCommunitiesProps {
  className?: string;
}

interface Community {
  id: string;
  name: string;
  description: string;
  // image_url: string | null;
  // type: 'community' | 'industry';
  // is_verified: boolean;
  created_at: string;
  members: Member[];
  total_members: number;
  header_image: string | null;
  avatar_image: string | null;
}

const RecommendedCommunities: React.FC<RecommendedCommunitiesProps> = ({ className = '' }) => {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentCommunities();
  }, []);

  const fetchRecentCommunities = async () => {
    try {
      // Get communities data
      const { data: communitiesData, error: communitiesError } = await supabase
        .from('communities')
        .select(`
          id,
          name,
          description,
          header_image,
          avatar_image,
          display_priority,
          display_highlight,
          members:community_members!community_id (
            user_id,
            users (
              id,
              first_name,
              profile_image_url
            )
          )
        `)
        .or('display_highlight.eq.true,display_priority.gte.500') // simple workaround to be able to suggest non-featured communities, until we implement proper "for you".
        .order('display_priority', { ascending: false })
        .order('name', { ascending: true })
        .limit(3);

      if (communitiesError) throw communitiesError;

      // Process communities with members
      const processedCommunities = (communitiesData || []).map((community: any) => {
        const members = (community.members || []).map((m: any) => ({
          user_id: m.user_id,
          first_name: m.users?.first_name ?? '',
          last_name: '',
          profile_image_url: m.users?.profile_image_url ?? null,
        }));

        // Get public URLs for images if they exist - currently unused
        // const headerImageUrl = community.header_image
        //   ? supabase.storage.from('community_images').getPublicUrl(community.header_image).data.publicUrl
        //   : null;

        // const avatarImageUrl = community.avatar_image
        //   ? supabase.storage.from('community_images').getPublicUrl(community.avatar_image).data.publicUrl
        //   : null;

        return {
          ...community,
          members,
          total_members: members.length,
          // header_image: headerImageUrl,
          // avatar_image: avatarImageUrl,
        };
      });

      setCommunities(processedCommunities);
    } catch (error) {
      console.error("Error fetching recent communities:", error);
      captureAppException(error, {
        tags: { context: 'recommended_communities', action: 'fetch_communities' },
      });
    } finally {
      setLoading(false);
    }
  };

  const getVariant = (index: number): 'entertainment' | 'enterprise' | 'retail' => {
    const variants: ('entertainment' | 'enterprise' | 'retail')[] = ['entertainment', 'enterprise', 'retail'];
    return variants[index % variants.length];
  };

  return (
    <div className={`bg-white rounded-2xl p-6 ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-bodyL font-bold text-greyscale-600">Recommended Communities</h2>
        <Link to="/communities">
          <Button
            variant="ghost"
            color="primary"
            size="small"
          >
            Go to Community
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : communities.length > 0 ? (
        <>
          {communities.length >= 2 && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {communities.slice(0, 2).map((community, index) => (
                <CommunityCard
                  key={community.id}
                  id={community.id}
                  title={community.name}
                  variant={getVariant(index)}
                  showShape={index === 0}
                  members={community.members}
                  totalMembers={community.total_members}
                // popularText={index === 0 ? 'Popular with those interested in Deep Learning' : 'Popular with those interested in Augmented Reality'}
                />
              ))}
            </div>
          )}

          {communities.length >= 3 && (
            <div>
              <CommunityCard
                id={communities[2].id}
                title={communities[2].name}
                variant="retail"
                members={communities[2].members}
                totalMembers={communities[2].total_members}
                showShape={true}
              // popularText="Popular with those interested in Digital Twins"
              />
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No communities found. Check back later!
        </div>
      )}
    </div>
  );
};

export default RecommendedCommunities; 